import { z } from 'zod';
import { StructuredContentSchema } from './meeting.js';
import { TopicSchema } from './topic.js';
/**
 * Claude tool-use schemas. Each tool's input_schema is the Zod → JSON Schema conversion
 * of the corresponding `*ToolInput` schema here. Claude's tool call output is validated
 * server-side against these schemas before being persisted.
 */
// ─── emit_minutes ─────────────────────────────────────────────────────────
// Main transform: takes raw input + metadata, emits full structured content.
export const EmitMinutesInputSchema = StructuredContentSchema;
// ─── emit_rewrite ─────────────────────────────────────────────────────────
// Highlight + instruction rewrite: returns the rewritten snippet only.
export const EmitRewriteInputSchema = z.object({
    rewrittenText: z.string().min(1),
    rationale: z.string().optional(),
});
// ─── emit_polish ──────────────────────────────────────────────────────────
// Grammar / tone pass: returns the full structured content with polished language.
export const EmitPolishInputSchema = StructuredContentSchema;
// ─── emit_translation ─────────────────────────────────────────────────────
// Translates all user-visible text; preserves structure.
export const EmitTranslationInputSchema = StructuredContentSchema;
// ─── suggest_title ────────────────────────────────────────────────────────
// Lightweight — Haiku.
export const SuggestTitleInputSchema = z.object({
    title: z.string().min(1).max(100),
});
// ─── generate_summary_email ───────────────────────────────────────────────
// Lightweight — Haiku. 2–3 sentence recap body for email.
export const GenerateSummaryEmailInputSchema = z.object({
    subject: z.string().min(1).max(200),
    body: z.string().min(1).max(1000),
});
// ─── detect_followup ──────────────────────────────────────────────────────
// Haiku — detects scheduling language and proposes follow-up event details.
//
// 3-state status so the UI can differentiate:
//   'none'     → no follow-up discussed; no prompt shown
//   'vague'    → follow-up agreed to, but no date/time mentioned →
//                ask user "want to schedule one?" before opening the
//                full invite dialog (empty date)
//   'specific' → specific date (optionally time) mentioned → open the
//                invite dialog with proposed date/title/attendees
//                prefilled
export const DetectFollowupInputSchema = z.object({
    status: z.enum(['none', 'vague', 'specific']),
    proposedDate: z.string().optional(), // ISO datetime — only when status=specific
    proposedTitle: z.string().optional(),
    proposedAttendees: z.array(z.string()).optional(),
    evidenceQuote: z.string().optional(),
});
// ─── extract_external_attendees ───────────────────────────────────────────
// Haiku — pulls non-OJB attendees from raw input.
export const ExtractExternalAttendeesInputSchema = z.object({
    groups: z.array(z.object({
        firm: z.string().min(1),
        members: z.array(z.string().min(1)),
    })),
});
// ─── detect_references ────────────────────────────────────────────────────
// Haiku — flags mentions of external documents.
export const DetectReferencesInputSchema = z.object({
    references: z.array(z.object({
        mention: z.string().min(1),
        context: z.string().optional(),
    })),
});
// ─── extract_action_items ────────────────────────────────────────────────
// Sonnet — scans the document for sentences that imply commitments but
// aren't already captured as action items. Returns proposals the user
// reviews + accepts per-item in a modal.
export const ExtractActionItemsInputSchema = z.object({
    proposed: z
        .array(z.object({
        description: z.string().min(1).max(500),
        assigneeName: z.string().min(1).max(200).optional(),
        dueDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        /** The bullet text that implied this commitment — shown to the
         *  user as context so they can audit the proposal. */
        sourceQuote: z.string().min(1).max(600),
        confidence: z.enum(['high', 'medium', 'low']),
    }))
        .max(20),
});
// ─── resolve_ambiguities ─────────────────────────────────────────────────
// Sonnet — for every bullet whose ambiguityFlag is set, propose a
// concrete resolution using surrounding context + project memory.
export const ResolveAmbiguitiesInputSchema = z.object({
    resolutions: z
        .array(z.object({
        topicIndex: z.number().int().min(0),
        bulletIndex: z.number().int().min(0),
        originalBody: z.string().min(1).max(2_000),
        originalFlag: z.string().min(1).max(500),
        proposedBody: z.string().min(1).max(2_000),
        rationale: z.string().min(1).max(500),
        confidence: z.enum(['high', 'medium', 'low']),
    }))
        .max(30),
});
// ─── consistency_check ───────────────────────────────────────────────────
// Sonnet — compares this meeting to the N most recent finalized meetings
// in the same project. Finds terminology drift, contradictions, and
// abandoned commitments. Purely informational — no auto-apply.
export const ConsistencyCheckInputSchema = z.object({
    terminologyDrift: z
        .array(z.object({
        currentTerm: z.string().min(1).max(200),
        priorTerm: z.string().min(1).max(200),
        priorMeetingId: z.string().min(1),
        rationale: z.string().min(1).max(500),
    }))
        .max(15),
    contradictions: z
        .array(z.object({
        currentStatement: z.string().min(1).max(500),
        priorStatement: z.string().min(1).max(500),
        priorMeetingId: z.string().min(1),
        rationale: z.string().min(1).max(500),
    }))
        .max(15),
    abandonedCommitments: z
        .array(z.object({
        description: z.string().min(1).max(500),
        priorMeetingId: z.string().min(1),
        priorMeetingDate: z.string().min(1),
        assignee: z.string().min(1).max(200).optional(),
    }))
        .max(20),
});
// ─── harvest_glossary ────────────────────────────────────────────────────
// Haiku — scans for acronyms and jargon terms in the document that
// aren't already in the firm glossary and proposes entries with
// expansions.
export const HarvestGlossaryInputSchema = z.object({
    proposed: z
        .array(z.object({
        term: z.string().min(1).max(120),
        proposedExpansion: z.string().min(1).max(500),
        kind: z.enum(['acronym', 'jargon']),
        sourceQuote: z.string().min(1).max(500),
    }))
        .max(25),
});
// ─── check_speaker_attribution ───────────────────────────────────────────
// Haiku — verifies that every bullet's `speaker` matches someone on the
// attendee list. Transcripts often misspell names or attribute to
// "Speaker 3" style placeholders; this finds and proposes fixes.
export const CheckSpeakerAttributionInputSchema = z.object({
    issues: z
        .array(z.object({
        topicIndex: z.number().int().min(0),
        bulletIndex: z.number().int().min(0),
        currentSpeaker: z.string().min(1).max(200),
        proposedSpeaker: z.string().max(200).nullable(),
        confidence: z.enum(['high', 'medium', 'low']),
        rationale: z.string().min(1).max(500),
    }))
        .max(40),
});
// ─── executive_summary ───────────────────────────────────────────────────
// Haiku — 3-5 sentence TL;DR of a finalized or near-finalized meeting.
// Used by the editor's AI enhancements panel to give the note-taker
// (and downstream readers) a skim-friendly paragraph. Not stored on
// the meeting row; rendered in a modal with Copy-to-clipboard.
export const ExecutiveSummaryInputSchema = z.object({
    summary: z
        .string()
        .min(20)
        .max(2_000)
        .describe('A 3-5 sentence plain-English summary of the meeting, written for a skim reader. No bullets, no headings - a single paragraph that opens with the headline outcome.'),
});
// ─── project_ask ──────────────────────────────────────────────────────────
// Sonnet — cross-meeting synthesis. Given a user question + the structured
// content of N candidate meetings (FTS top hits for the project), returns
// a woven narrative with cited meeting ids.
//
// Shape: narrative summary + structured chunks so the UI can render each
// section distinctly (and so the cited meetingIds can be rendered as
// clickable date badges per item).
export const ProjectAskInputSchema = z.object({
    summary: z
        .string()
        .min(1)
        .max(3_000)
        .describe("2-4 paragraph narrative answer to the user's question, woven from the provided meetings. Must reflect only what's in those meetings - never invent facts."),
    keyPoints: z
        .array(z.object({
        text: z.string().min(1).max(400),
        meetingIds: z
            .array(z.string())
            .min(1)
            .describe('At least one meetingId that supports this point.'),
    }))
        .max(10),
    openQuestions: z
        .array(z.object({
        text: z.string().min(1).max(400),
        meetingIds: z.array(z.string()).min(1),
    }))
        .max(10)
        .describe('Questions the meetings raised but never resolved. Leave empty when the question was fully answered.'),
    timeline: z
        .array(z.object({
        meetingId: z.string(),
        meetingDate: z.string(),
        contribution: z
            .string()
            .min(1)
            .max(300)
            .describe("One sentence on what this meeting added to the topic."),
    }))
        .max(20)
        .describe('Chronological view of how the topic evolved across meetings. Leave empty when the topic only surfaced once.'),
});
// ─── update_style_notes ───────────────────────────────────────────────────
// Haiku — consumes (AI-original, user-final, existing notes) and returns
// the compacted notes string. Cap at 2000 chars so the profile stays
// lean enough to include as a cached transform prefix without bloating
// every prompt. Roughly 300-400 tokens of guidance.
export const StyleNotesInputSchema = z.object({
    notes: z.string().max(2000),
});
/**
 * Tool registry — maps task names to their input schemas.
 * Consumed by the Claude service layer to build tool definitions + validate responses.
 */
export const CLAUDE_TOOLS = {
    emit_minutes: EmitMinutesInputSchema,
    emit_rewrite: EmitRewriteInputSchema,
    emit_polish: EmitPolishInputSchema,
    emit_translation: EmitTranslationInputSchema,
    suggest_title: SuggestTitleInputSchema,
    generate_summary_email: GenerateSummaryEmailInputSchema,
    detect_followup: DetectFollowupInputSchema,
    extract_external_attendees: ExtractExternalAttendeesInputSchema,
    detect_references: DetectReferencesInputSchema,
    update_style_notes: StyleNotesInputSchema,
    project_ask: ProjectAskInputSchema,
    executive_summary: ExecutiveSummaryInputSchema,
    extract_action_items: ExtractActionItemsInputSchema,
    resolve_ambiguities: ResolveAmbiguitiesInputSchema,
    consistency_check: ConsistencyCheckInputSchema,
    harvest_glossary: HarvestGlossaryInputSchema,
    check_speaker_attribution: CheckSpeakerAttributionInputSchema,
};
// Placeholder: rewrite response uses topic content rather than its own schema in some paths
export const BulletEditResultSchema = TopicSchema.pick({ bullets: true });
//# sourceMappingURL=claudeTools.js.map